import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../../utils';
import { ArrowLeft, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import authService from '../../services/auth.service';

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submitHandler = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await authService.forgotPassword(email);
      setSent(true);
    } catch (err) {
      toast.error(getError(err));
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width: '100%',
    backgroundColor: '#F5F5F5',
    border: '1px solid #E8E8E8',
    borderRadius: '8px',
    padding: '12px 14px',
    fontSize: '14px',
    color: '#1A1A1A',
    outline: 'none',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '12px',
    fontWeight: '500',
    color: '#7C7C7C',
    marginBottom: '6px',
  };

  return (
    <div
      className='flex min-h-screen items-center justify-center px-4 py-10'
      style={{ backgroundColor: 'var(--background-color, #F4F4F4)' }}
    >
      <Helmet>
        <title>{t('auth.resetPassword')}</title>
      </Helmet>

      <div className='w-full max-w-sm rounded-2xl shadow-lg overflow-hidden bg-white'>
        {/* Header */}
        <div
          className='flex items-center px-5 py-4'
          style={{
            backgroundColor: 'var(--primaryColor)',
            color: 'var(--navFontColor)',
          }}
        >
          <button
            type='button'
            onClick={() => navigate(-1)}
            className='mr-3 flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-white/20 transition-colors'
            style={{ color: 'var(--navFontColor)' }}
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className='text-base font-semibold' style={{ color: 'var(--navFontColor)' }}>
            {t('auth.resetPassword')}
          </h1>
        </div>

        {/* Body */}
        <div className='px-6 py-6'>
          {sent ? (
            /* Success state */
            <div className='flex flex-col items-center gap-4 py-4 text-center'>
              <div
                className='flex h-14 w-14 items-center justify-center rounded-full'
                style={{ backgroundColor: 'var(--secondaryColor, #6366f1)' }}
              >
                <Mail size={24} color='#fff' />
              </div>
              <div>
                <p className='text-sm font-semibold text-gray-800' style={{ marginBottom: '6px' }}>
                  {t('auth.resetEmailSent', 'Revisá tu correo')}
                </p>
                <p className='text-xs text-gray-500'>
                  {t(
                    'auth.resetEmailHint',
                    'Te enviamos instrucciones para restablecer tu contraseña a'
                  )}{' '}
                  <strong>{email}</strong>
                </p>
              </div>
              <Link
                to='/signin'
                style={{
                  fontSize: '13px',
                  color: 'var(--secondaryColor)',
                  fontWeight: '600',
                  textDecoration: 'none',
                }}
              >
                {t('auth.backToSignIn', 'Volver al inicio de sesión')}
              </Link>
            </div>
          ) : (
            /* Email form */
            <form onSubmit={submitHandler} className='space-y-4'>
              <p
                style={{
                  fontSize: '13px',
                  color: '#7C7C7C',
                  marginBottom: '8px',
                }}
              >
                {t(
                  'auth.resetPasswordHint',
                  'Ingresá tu correo y te enviaremos instrucciones para restablecer tu contraseña.'
                )}
              </p>

              <div>
                <label style={labelStyle}>{t('auth.email')}</label>
                <input
                  type='email'
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value.toLowerCase())}
                  placeholder='mail@ejemplo.com'
                  style={inputStyle}
                  autoComplete='email'
                />
              </div>

              <button
                type='submit'
                disabled={submitting}
                className='w-full rounded-xl py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50'
                style={{
                  backgroundColor: 'var(--secondaryColor)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                  border: 'none',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  marginTop: '8px',
                }}
              >
                {submitting
                  ? t('common.loading')
                  : t('auth.sendInstructions', 'Enviar instrucciones')}
              </button>

              <p className='text-center' style={{ fontSize: '13px', color: '#7C7C7C' }}>
                <Link
                  to='/signin'
                  style={{
                    color: 'var(--secondaryColor)',
                    fontWeight: '600',
                    textDecoration: 'none',
                  }}
                >
                  {t('auth.backToSignIn', 'Volver al inicio de sesión')}
                </Link>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
